import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Upload, Download, Trash2, FileText, Image, Eye } from "lucide-react";
import DocumentPreviewDialog from "./DocumentPreviewDialog";
import { buildHabilitationDocPreview, downloadHabilitationDocument } from "../lib/habilitationDocuments";

const HabilitationDocuments = ({ habilitationId, employeeId, employeeName }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [uploadData, setUploadData] = useState({
    filename: '',
    file: null,
    preview: null
  });
  const [previewDoc, setPreviewDoc] = useState(null);

  useEffect(() => {
    if (habilitationId) {
      fetchDocuments();
    }
  }, [habilitationId]);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/habilitations/${habilitationId}/documents`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10MB)');
      return;
    }

    // Check file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez PDF, JPG ou PNG');
      return;
    }

    // Read file as base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1]; // Remove data:image/jpeg;base64, prefix
      setUploadData({
        filename: file.name,
        file: base64,
        preview: URL.createObjectURL(file)
      });
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!uploadData.file) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Determine file type from filename
      const ext = uploadData.filename.split('.').pop().toLowerCase();
      const fileType = ext === 'pdf' ? 'pdf' : ext;

      const payload = {
        habilitation_id: habilitationId,
        employee_id: employeeId,
        filename: uploadData.filename,
        content_base64: uploadData.file,
        file_type: fileType
      };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/habilitations/${habilitationId}/documents`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        toast.success('Document uploadé avec succès');
        setIsDialogOpen(false);
        setUploadData({ filename: '', file: null, preview: null });
        fetchDocuments();
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Erreur lors de l\'upload du document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (docId, filename) => {
    try {
      await downloadHabilitationDocument(docId, filename);
      toast.success('Document téléchargé');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handlePreview = async (doc) => {
    try {
      const preview = await buildHabilitationDocPreview(doc);
      if (!preview) {
        toast.error('Aucune donnée disponible pour ce document');
        return;
      }
      setPreviewDoc(preview);
    } catch (error) {
      console.error('Error previewing document:', error);
      toast.error("Erreur lors du chargement de l'aperçu");
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/habilitations/documents/${docId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        toast.success('Document supprimé');
        fetchDocuments();
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType === 'pdf') return <FileText className="w-5 h-5 text-red-500" />;
    return <Image className="w-5 h-5 text-blue-500" />;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Documents joints</CardTitle>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-[#0F5156] hover:bg-[#0a3a3e]">
                <Upload className="w-4 h-4 mr-1" />
                Ajouter
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Ajouter un document</DialogTitle>
                <DialogDescription>
                  Téléchargez un document pour {employeeName} (PDF, JPG ou PNG, max 10MB)
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="file">Fichier</Label>
                  <Input
                    id="file"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileSelect}
                  />
                </div>

                {uploadData.preview && (
                  <div className="border rounded p-4 bg-slate-50">
                    <p className="text-sm font-medium mb-2">Aperçu:</p>
                    <p className="text-sm text-slate-600">{uploadData.filename}</p>
                    {uploadData.filename.endsWith('.pdf') ? (
                      <div className="flex items-center gap-2 mt-2">
                        <FileText className="w-8 h-8 text-red-500" />
                        <span className="text-sm">Fichier PDF</span>
                      </div>
                    ) : (
                      <img 
                        src={uploadData.preview} 
                        alt="Preview" 
                        className="mt-2 max-w-full h-auto max-h-48 rounded"
                      />
                    )}
                  </div>
                )}

                <div className="flex gap-2">
                  <Button 
                    onClick={handleUpload}
                    disabled={!uploadData.file || uploading}
                    className="bg-[#0F5156] hover:bg-[#0a3a3e]"
                  >
                    {uploading ? 'Upload...' : 'Télécharger'}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setIsDialogOpen(false);
                      setUploadData({ filename: '', file: null, preview: null });
                    }}
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-slate-500 text-center py-4">Chargement...</p>
        ) : documents.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">
            Aucun document. Cliquez sur "Ajouter" pour uploader un fichier.
          </p>
        ) : (
          <div className="space-y-2">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-3 border rounded hover:bg-slate-50"
              >
                <div className="flex items-center gap-3">
                  {getFileIcon(doc.file_type)}
                  <div>
                    <p className="text-sm font-medium">{doc.filename}</p>
                    <p className="text-xs text-slate-500">
                      Uploadé le {new Date(doc.uploaded_date).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    title="Aperçu"
                    onClick={() => handlePreview(doc)}
                  >
                    <Eye className="w-4 h-4 text-[#0F5156]" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    title="Télécharger"
                    onClick={() => handleDownload(doc.id, doc.filename)}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(doc.id)}
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Aperçu de document */}
      <DocumentPreviewDialog
        preview={previewDoc}
        onClose={() => setPreviewDoc(null)}
        onDownload={() => previewDoc?.sourceDoc && handleDownload(previewDoc.sourceDoc.id, previewDoc.sourceDoc.filename)}
      />
    </Card>
  );
};

export default HabilitationDocuments;
